import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import Loader from '../Loader/Loader';

interface TextInputProps {
  id: string;
  name?: string;
  type?: string;
  value: string;
  label?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  autoComplete?: boolean;
  inputRef?: React.RefObject<HTMLInputElement>;
  handleInputFocus?: React.FocusEventHandler<HTMLInputElement>;
  handleInputChange: React.ChangeEventHandler<HTMLInputElement>;
  handleInputBlur?: React.FocusEventHandler<HTMLInputElement>;
  handleInputKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  errorMessage?: string;
  shouldHighlightError?: boolean; // Pass true to highlight input in red and display error icon to its right
  isLoading?: boolean;
  inputClassNames?: string;
  inline?: boolean;
}

const TextInput: React.FC<TextInputProps> = ({
  id,
  name,
  type,
  value,
  label,
  inputRef,
  disabled,
  autoFocus,
  placeholder,
  autoComplete,
  handleInputBlur,
  handleInputFocus,
  handleInputChange,
  handleInputKeyDown,
  errorMessage = '',
  shouldHighlightError,
  isLoading = false,
  inputClassNames = '',
  inline,
}: TextInputProps) => {
  const hasError = !!errorMessage;
  return (
    <div
      className={classNames({
        'flex gap-4': !!inline,
      })}
    >
      {label && (
        <div className="flex mb-1">
          <label data-testid={`${id}_label`} htmlFor={id} className="block text-sm font-medium text-gray-700">
            {label}
          </label>
        </div>
      )}
      <div className="flex justify-end items-center">
        <input
          id={id}
          type={type ?? 'text'}
          name={name || id}
          value={value}
          ref={inputRef}
          data-testid={id}
          placeholder={placeholder}
          className={twMerge(
            classNames('w-full h-12 border outline-none focus:outline-none block sm:text-sm shadow-sm rounded-md', {
              'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500': !hasError && !shouldHighlightError,
              'border-2 border-red-500 focus:ring-red-500 focus:border-red-500': hasError || shouldHighlightError,
              'bg-gray-100': !!disabled,
            }),
            inputClassNames
          )}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          disabled={disabled || isLoading}
          autoComplete={autoComplete ? 'on' : 'off'}
          autoFocus={autoFocus ?? false}
          data-qa={id}
        />
        <Loader show={isLoading} className="absolute mr-3" spin />
        {(hasError || shouldHighlightError) && (
          <div className="absolute pr-3 flex items-center pointer-events-none">
            <ExclamationCircleIcon className="h-5 text-red-500" />
          </div>
        )}
      </div>
      {hasError && errorMessage && (
        <div className="error text-red-500 text-sm mb-2 mt-1" data-testid="errorMessage">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default TextInput;
